import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles, aboutInfo } from '@/mocks/hotelData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <header><Navbar /></header>
        <main className="pt-16 md:pt-18">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto py-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除。</p>
            <button
              onClick={() => navigate('/category/articles')}
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors duration-200 cursor-pointer text-sm"
            >
              返回攻略列表
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && (a.category === article.category || a.tags.some((t) => article.tags.includes(t))))
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        author: { '@type': 'Person', name: article.author },
        url: `https://www.grandlisboa.com/article/${article.slug}`,
        publisher: { '@type': 'Organization', name: aboutInfo.brandName, url: 'https://www.grandlisboa.com' },
        image: article.imageUrl,
      }} />
      <SchemaOrg type="WebPage" data={{
        name: `${article.title} - ${aboutInfo.brandName}`,
        description: article.excerpt,
        url: `https://www.grandlisboa.com/article/${article.slug}`,
        isPartOf: { '@type': 'WebSite', name: aboutInfo.brandName, url: 'https://www.grandlisboa.com' },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.grandlisboa.com' },
          { '@type': 'ListItem', position: 2, name: '入住攻略', item: 'https://www.grandlisboa.com/category/articles' },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />

      <header><Navbar /></header>

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="pt-6">
              <Breadcrumb items={[
                { label: '首页', path: '/' },
                { label: '入住攻略', path: '/category/articles' },
                { label: article.title },
              ]} />
            </div>
          </div>
        </div>

        <article className="w-full px-4 md:px-6 max-w-3xl mx-auto py-10 md:py-14">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">· {article.author}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
          </div>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-64 md:h-80 object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
              prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
              prose-p:my-4 prose-p:leading-relaxed
              prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-16">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">相关攻略推荐</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {relatedArticles.map((ra) => (
                  <article
                    key={ra.id}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/article/${ra.slug}`)}
                  >
                    <div className="relative h-44 md:h-52 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        title={`${ra.title} - 新葡京酒店攻略`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 md:p-6">
                      <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                        <span>{ra.date}</span>
                        <span>·</span>
                        <span>{ra.author}</span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {ra.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">{ra.excerpt}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}